import React from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { blogPosts } from "../mock";
import { ArrowLeft, Calendar, User, Tag, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";

const SingleBlog = () => {
  const { id } = useParams();
  const post = blogPosts.find(p => p.id === parseInt(id));

  if (!post) {
    return (
      <div className="min-h-screen bg-[#000000] pt-24 flex items-center justify-center">
        <SEO 
          title="Blog Post Not Found"
          description="The requested blog post could not be found."
          url={`/blog/${id}`}
        />
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#F4D03F] mb-4">Blog Post Not Found</h1>
          <Link to="/blog">
            <Button className="btn-primary">
              <ArrowLeft size={20} className="mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Get related posts (exclude current post)
  const relatedPosts = blogPosts
    .filter(p => p.id !== post.id && (p.category === post.category || Math.random() > 0.5))
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-[#000000] pt-20 sm:pt-24">
      <SEO 
        title={post.title}
        description={post.excerpt}
        keywords={`${post.category}, ${post.title}, book publishing, writing tips, ${post.author}`}
        image={post.image}
        url={`/blog/${post.id}`}
        type="article"
      />
      {/* Back Button */}
      <section className="py-6 sm:py-8 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 sm:px-6">
          <Link to="/blog">
            <Button className="btn-secondary text-sm sm:text-base">
              <ArrowLeft size={18} className="mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </section>

      {/* Blog Post Content */}
      <section className="py-8 sm:py-12 bg-[#000000]">
        <div className="container mx-auto px-4 sm:px-6">
          <article className="max-w-4xl mx-auto">
            {/* Featured Image */}
            <div className="aspect-video overflow-hidden rounded-lg sm:rounded-2xl mb-6 sm:mb-8">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-6 mb-4 sm:mb-6 text-[#888680]">
              <div className="flex items-center gap-2">
                <Tag size={16} className="text-[#F4D03F]" />
                <span className="text-[#F4D03F] font-semibold uppercase text-xs sm:text-sm">{post.category}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-[#F4D03F]" />
                <span className="text-xs sm:text-sm">{post.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <User size={16} className="text-[#F4D03F]" />
                <span className="text-xs sm:text-sm">{post.author}</span>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#F4D03F] mb-4 sm:mb-6 leading-tight uppercase">
              {post.title}
            </h1>

            {/* Excerpt */}
            <p className="text-base sm:text-lg md:text-xl text-[#dfddd6] mb-6 sm:mb-8 italic border-l-4 border-[#F4D03F] pl-4 sm:pl-6">
              {post.excerpt}
            </p>

            {/* Content */}
            <div className="prose prose-invert prose-lg max-w-none">
              <div className="text-[#dfddd6] leading-relaxed space-y-4 sm:space-y-6">
                {post.content.split("\n\n").map((paragraph, index) => {
                  // Check if paragraph is a heading (starts with **)
                  if (paragraph.trim().startsWith("**") && paragraph.trim().endsWith("**")) {
                    const heading = paragraph.replace(/\*\*/g, "");
                    return (
                      <h2 key={index} className="text-xl sm:text-2xl font-bold text-[#F4D03F] mt-6 sm:mt-8 mb-3 sm:mb-4 uppercase">
                        {heading}
                      </h2>
                    );
                  }
                  // Regular paragraph
                  return (
                    <p key={index} className="text-base sm:text-lg" style={{ fontSize: "clamp(1rem, 2vw, 1.25rem)" }}>
                      {paragraph.split("**").map((part, i) => 
                        i % 2 === 0 ? part : <strong key={i} className="text-[#F4D03F]">{part}</strong>
                      )}
                    </p>
                  );
                })}
              </div>
            </div>

            {/* Author Box */}
            <div className="mt-8 sm:mt-12 p-4 sm:p-6 bg-[#302f2c] rounded-lg border border-[#3f4816]">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                  <User size={24} className="text-[#D4AF37] sm:w-8 sm:h-8" />
                </div>
                <div>
                  <h3 className="text-[#F4D03F] font-bold text-base sm:text-xl">About {post.author}</h3>
                  <p className="text-[#888680] text-sm sm:text-base">
                    Publishing industry expert and writer dedicated to helping authors succeed.
                  </p>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 sm:py-20 bg-[#302f2c]">
          <div className="container mx-auto px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-8 sm:mb-12 text-center uppercase">Related Articles</h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
              {relatedPosts.map((relatedPost) => (
                <Card key={relatedPost.id} className="bg-[#000000] border-[#3f4816] overflow-hidden hover:border-[#F4D03F] transition-all duration-300 group">
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <CardHeader className="pb-4 p-4 sm:p-6">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <span className="text-[#F4D03F] text-[10px] sm:text-xs uppercase font-semibold">{relatedPost.category}</span>
                      <span className="text-[#888680] text-[10px] sm:text-xs">{relatedPost.date}</span>
                    </div>
                    <CardTitle className="text-[#dfddd6] text-base sm:text-lg group-hover:text-[#F4D03F] transition-colors leading-tight">
                      {relatedPost.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 p-4 sm:p-6">
                    <Link to={`/blog/${relatedPost.id}`}>
                      <Button className="btn-secondary w-full group text-sm sm:text-base">
                        Read More
                        <ArrowRight size={14} className="ml-2 transition-transform duration-200 group-hover:translate-x-1" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default SingleBlog;